// client/src/components/profissionais/sections/ProfissionalAgendaSection.js 
import React, { useState, useEffect } from 'react';
import { useProfissionalPorUsuario } from '../../../hooks/useProfissionais';
import useAgendas from '../../../hooks/useAgendas'; 
import { useUnidadesPorConta } from '../../../hooks/useUnidades'; 
import { Loader2, Save, XCircle, AlertCircle, Calendar, Settings, Lock } from 'lucide-react'; // ✅ Import do Lock adicionado aqui!
import useAlert from '../../../hooks/useAlert';

const extractIdStr = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    const id = obj._id || obj.id;
    return id ? String(id) : null;
};

const PrazoInput = ({ prazo, onPrazoChange, disabled }) => (
    <div className="input-group flex">
        <input 
            type="number" 
            name="valor" 
            value={prazo?.valor || ''} 
            onChange={onPrazoChange} 
            className={`form-input flex-1 ${disabled ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
            min="0"
            disabled={disabled}
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        />
        <select 
            name="unidade" 
            value={prazo?.unidade || 'horas'} 
            onChange={onPrazoChange} 
            className={`form-input ${disabled ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
            disabled={disabled}
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, width: '120px' }}
        >
            <option value="minutos">Minutos</option>
            <option value="horas">Horas</option>
            <option value="dias">Dias</option>
        </select>
    </div>
);

const ProfissionalAgendaSection = ({ user, contextData, onUpdateSuccess }) => {
    const { setAlert } = useAlert();
    const userIdValido = extractIdStr(user);
    const contaIdValida = extractIdStr(contextData?.contaId);

    const { useAgendaByEntidadeQuery, updateAgenda } = useAgendas(); 
    const { data: agendaResponse, isLoading: loadingAgenda } = useAgendaByEntidadeQuery('User', userIdValido);

    const { data: profResponse, isLoading: loadingProfissional } = useProfissionalPorUsuario(userIdValido, {
        enabled: !!userIdValido
    });

    const { data: unidadesData } = useUnidadesPorConta(contaIdValida, {
        enabled: !!contaIdValida, ativo: true
    });
    const unidades = unidadesData?.data || [];

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        nomeExibicao: '',
        permiteAgendamentoOnline: true,
        intervaloGradeHorarios: 60,
        horarioInicio: 7,
        horarioFim: 22,
        prazoCancelamentoSemPagamento: { valor: 24, unidade: 'horas' },
        acaoCancelamentoSemPagamento: 'Ser Avisado',
        disponibilidadeFuturaEmDias: 30,
        antecedenciaMinimaParaAgendar: { valor: 1, unidade: 'horas' },
        permiteCancelamentoOnline: 'Automático',
        prazoMaximoCancelamentoOnline: { valor: 24, unidade: 'horas' },
        visibilidade: 'publica_na_unidade', 
        unidadesVinculadas: [], 
        corAgenda: '#3788d8',
        exibirNaAgenda: true
    });

    useEffect(() => {
        if (loadingAgenda || loadingProfissional) return;
        
        const agendaData = agendaResponse?.data || agendaResponse;
        const profData = profResponse?.data || profResponse;
        const userNameFallback = profData?.user?.name || 'Profissional';

        if (agendaData && (agendaData.id || agendaData._id)) {
            const agendaInfo = agendaData.configuracoes || {};
            const permissoesInfo = agendaData.permissoes || {};
            const metaInfo = agendaData.metadata || {};

            setFormData({
                nomeExibicao: agendaData.nomeExibicao || `Agenda de ${userNameFallback}`,
                permiteAgendamentoOnline: agendaInfo.permiteAgendamentoOnline !== false,
                intervaloGradeHorarios: Number(agendaInfo.intervaloGradeHorarios) || 60,
                horarioInicio: Number(agendaInfo.horarioInicio) ?? 7,
                horarioFim: Number(agendaInfo.horarioFim) ?? 22,
                
                prazoCancelamentoSemPagamento: {
                    valor: Number(agendaInfo.prazoCancelamentoSemPagamento?.valor) || 0,
                    unidade: agendaInfo.prazoCancelamentoSemPagamento?.unidade || 'horas'
                },
                acaoCancelamentoSemPagamento: agendaInfo.acaoCancelamentoSemPagamento || 'Ser Avisado',
                disponibilidadeFuturaEmDias: Number(agendaInfo.disponibilidadeFuturaEmDias) || 30,
                antecedenciaMinimaParaAgendar: {
                    valor: Number(agendaInfo.antecedenciaMinimaParaAgendar?.valor) || 1,
                    unidade: agendaInfo.antecedenciaMinimaParaAgendar?.unidade || 'horas'
                },
                permiteCancelamentoOnline: agendaInfo.permiteCancelamentoOnline || 'Automático',
                prazoMaximoCancelamentoOnline: {
                    valor: Number(agendaInfo.prazoMaximoCancelamentoOnline?.valor) || 0,
                    unidade: agendaInfo.prazoMaximoCancelamentoOnline?.unidade || 'horas'
                },
                visibilidade: permissoesInfo.visibilidade || 'publica_na_unidade',
                unidadesVinculadas: (permissoesInfo.unidadesVinculadas || []).map(extractIdStr),
                corAgenda: metaInfo.corAgenda || '#3788d8',
                exibirNaAgenda: metaInfo.exibirNaAgenda !== false
            });
        } else {
            setFormData(prev => ({
                ...prev,
                nomeExibicao: `Agenda de ${userNameFallback}`
            }));
        }
    }, [agendaResponse, loadingAgenda, profResponse, loadingProfissional]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = value;
        
        if (type === 'checkbox') {
            finalValue = checked;
        } else if (type === 'number' || ['intervaloGradeHorarios'].includes(name)) {
            finalValue = value === '' ? 0 : Number(value);
            if (name === 'horarioInicio' || name === 'horarioFim') {
                if (finalValue < 0) finalValue = 0;
                if (finalValue > 23) finalValue = 23;
            }
        }
        
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const onPrazoChange = (parentKey, e) => {
        const { name, value } = e.target;
        const valorFinal = name === 'valor' && value !== '' ? parseInt(value, 10) : value;
        setFormData(prev => ({ 
            ...prev, 
            [parentKey]: { 
                ...(prev[parentKey] || {}), 
                [name]: valorFinal 
            }
        }));
    };

    const handleSave = async (e) => {
        if(e) e.preventDefault();
        try {
            if (!userIdValido) {
                setAlert('ID do usuário não encontrado para vincular a agenda', 'danger');
                return;
            }

            if (!formData.nomeExibicao?.trim()) {
                setAlert('O Nome de Exibição da agenda é obrigatório.', 'warning');
                return;
            }

            setIsSaving(true);

            const payload = {
                tipoEntidade: 'User',
                entidadeId: userIdValido, 
                account: contaIdValida,
                nomeExibicao: formData.nomeExibicao.trim(),
                configuracoes: {
                    permiteAgendamentoOnline: formData.permiteAgendamentoOnline,
                    intervaloGradeHorarios: Number(formData.intervaloGradeHorarios),
                    horarioInicio: Number(formData.horarioInicio),
                    horarioFim: Number(formData.horarioFim),
                    prazoCancelamentoSemPagamento: {
                        valor: Number(formData.prazoCancelamentoSemPagamento.valor),
                        unidade: formData.prazoCancelamentoSemPagamento.unidade
                    },
                    acaoCancelamentoSemPagamento: formData.acaoCancelamentoSemPagamento,
                    disponibilidadeFuturaEmDias: Number(formData.disponibilidadeFuturaEmDias),
                    antecedenciaMinimaParaAgendar: {
                        valor: Number(formData.antecedenciaMinimaParaAgendar.valor),
                        unidade: formData.antecedenciaMinimaParaAgendar.unidade
                    },
                    permiteCancelamentoOnline: formData.permiteCancelamentoOnline,
                    prazoMaximoCancelamentoOnline: {
                        valor: Number(formData.prazoMaximoCancelamentoOnline.valor),
                        unidade: formData.prazoMaximoCancelamentoOnline.unidade
                    }
                },
                permissoes: {
                    visibilidade: formData.visibilidade,
                    unidadesVinculadas: formData.unidadesVinculadas 
                },
                metadata: {
                    corAgenda: formData.corAgenda,
                    exibirNaAgenda: formData.exibirNaAgenda
                }
            };

            await updateAgenda.mutateAsync({
                entidadeId: userIdValido,
                agendaData: payload
            });

            setAlert('Configurações da agenda salvas com sucesso!', 'success');
            setIsEditing(false);
            if (onUpdateSuccess) onUpdateSuccess();

        } catch (error) {
            console.error('❌ Erro ao salvar agenda:', error);
            const detailMsg = error.response?.data?.error || error.message;
            setAlert(`Erro ao salvar a agenda: ${detailMsg}`, 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    if (loadingProfissional || loadingAgenda) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-600" size={40} />
            </div>
        );
    }

    if (!userIdValido) {
        return (
            <div className="text-center py-12">
                <AlertCircle size={48} className="mx-auto text-yellow-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Usuário não encontrado</h3>
            </div>
        );
    }

    return (
        <div className="form-content">
            {/* CABEÇALHO PADRONIZADO */}
            <div className="form-header flex items-center justify-between mb-4">
                <div>
                    <h3 className="form-title flex items-center mb-0">
                        <Calendar size={20} className="inline mr-2 text-primary"/> Configurações da Agenda
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Defina horários, cor, cancelamentos e regras de visibilidade.</p>
                </div>
                
                {!isEditing ? (
                    <button 
                        type="button"
                        onClick={() => setIsEditing(true)} 
                        className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                    >
                        <Settings size={16} className="mr-2 text-gray-500" /> Editar Agenda
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button 
                            type="button" 
                            onClick={() => setIsEditing(false)} 
                            className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                        >
                            <XCircle size={16} className="mr-2 text-gray-500" /> Cancelar
                        </button>
                        <button 
                            type="button" 
                            onClick={handleSave} 
                            disabled={isSaving} 
                            className="action-button action-button-primary bg-primary text-white shadow-sm"
                        >
                            {isSaving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                            Salvar Agenda
                        </button>
                    </div>
                )}

                <div style={{ display: 'none' }}>
                    <button id="trigger-save-agenda" type="button" onClick={handleSave}></button>
                </div>
            </div>

            <div className="form-tabs-content p-0">
                <fieldset className="form-section" disabled={!isEditing || isSaving}>
                    <div className="form-grid-container">
                        
                        <div className="form-group md:col-span-2 pb-4 mb-2 border-b border-gray-100">
                            <label className="required font-medium text-gray-800">Nome de Exibição da Agenda</label>
                            <input 
                                type="text" 
                                name="nomeExibicao" 
                                value={formData.nomeExibicao} 
                                onChange={handleChange} 
                                className={`form-input mt-1 md:w-2/3 ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                placeholder='Ex: Agenda Dr. João'
                                required
                                disabled={!isEditing}
                            />
                            <p className="text-xs text-gray-500 mt-1">Este é o nome que aparecerá no calendário principal.</p>
                        </div>

                        <div className="form-group">
                            <label>Cor da Agenda</label>
                            <div className="flex items-center gap-2">
                                <input 
                                    type="color" 
                                    name="corAgenda" 
                                    value={formData.corAgenda} 
                                    onChange={handleChange} 
                                    className={`w-10 h-8 rounded cursor-pointer ${!isEditing ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    disabled={!isEditing}
                                />
                                <span className="text-sm text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                    {formData.corAgenda}
                                </span>
                            </div>
                        </div>

                        <div className="form-group md:col-span-2 mt-2">
                            <label className="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    name="exibirNaAgenda" 
                                    checked={formData.exibirNaAgenda} 
                                    onChange={handleChange} 
                                    className="form-checkbox w-4 h-4"
                                    disabled={!isEditing}
                                />
                                <span className="checkbox-label-text font-medium text-gray-800 ml-2">Exibir profissional na agenda principal da recepção</span>
                            </label>
                        </div>
                        
                        <div className="form-group md:col-span-2 mt-4 pt-4 border-t border-gray-100">
                            <label className="font-medium text-gray-800 block mb-1">Visibilidade (Controle de Acesso ABAC)</label>
                            <select 
                                name="visibilidade" 
                                value={formData.visibilidade} 
                                onChange={handleChange} 
                                className={`form-input md:w-1/2 ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                disabled={!isEditing}
                            >
                                <option value="publica_na_unidade">Pública na Unidade (Padrão)</option>
                                <option value="publica_na_conta">Pública em toda a Conta</option>
                                <option value="privada">Privada (Apenas equipe autorizada)</option>
                            </select>

                            {formData.visibilidade === 'publica_na_unidade' && (
                                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex flex-col gap-1 p-3 bg-blue-50 text-blue-800 rounded border border-blue-100">
                                        <div className="flex items-center font-medium">
                                            <Lock size={16} className="mr-2"/> Vínculo Gerenciado pelo Sistema
                                        </div>
                                        <p className="text-sm mt-1 text-blue-700">A visibilidade desta agenda é herdada automaticamente das unidades em que este profissional atua (Configuradas na aba de Unidades e Serviços).</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-group md:col-span-2 mt-4 pt-4 border-t border-gray-100">
                            <label className="checkbox-label font-medium text-gray-800">
                                <input 
                                    type="checkbox" 
                                    name="permiteAgendamentoOnline" 
                                    id="permiteAgendamentoOnlineUser" 
                                    checked={formData.permiteAgendamentoOnline} 
                                    onChange={handleChange} 
                                    className="form-checkbox w-4 h-4" 
                                    disabled={!isEditing}
                                />
                                <span className="ml-2">Permitir que clientes agendem sozinhos online</span>
                            </label>
                            <p className="text-xs text-gray-500 ml-6 mt-1">Ao desativar, o profissional só poderá ser agendado pela recepção.</p>
                        </div>

                        <div className="form-group mt-2">
                            <label>Intervalo entre horários (minutos)</label>
                            <select 
                                name="intervaloGradeHorarios" 
                                value={formData.intervaloGradeHorarios} 
                                onChange={handleChange} 
                                className={`form-input ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                disabled={!isEditing}
                            >
                                <option value="10">10 minutos</option>
                                <option value="15">15 minutos</option>
                                <option value="20">20 minutos</option>
                                <option value="30">30 minutos</option>
                                <option value="40">40 minutos</option>
                                <option value="45">45 minutos</option>
                                <option value="60">1 hora</option>
                            </select>
                        </div>

                        <div className="form-group mt-2">
                            <label>Dias de disponibilidade futura</label>
                            <input 
                                type="number" 
                                name="disponibilidadeFuturaEmDias" 
                                value={formData.disponibilidadeFuturaEmDias} 
                                onChange={handleChange} 
                                className={`form-input ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`} 
                                min="1" 
                                max="365"
                                disabled={!isEditing}
                            />
                        </div>

                        <div className="form-group">
                            <label>Início do expediente (Hora)</label>
                            <input 
                                type="number" 
                                name="horarioInicio" 
                                value={formData.horarioInicio} 
                                onChange={handleChange} 
                                className={`form-input ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`} 
                                min="0" 
                                max="23"
                                disabled={!isEditing}
                            />
                        </div>

                        <div className="form-group">
                            <label>Fim do expediente (Hora)</label>
                            <input 
                                type="number" 
                                name="horarioFim" 
                                value={formData.horarioFim} 
                                onChange={handleChange} 
                                className={`form-input ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`} 
                                min="0" 
                                max="23"
                                disabled={!isEditing}
                            />
                        </div>

                        <div className="form-group">
                            <label>Antecedência mínima para agendar</label>
                            <PrazoInput 
                                prazo={formData.antecedenciaMinimaParaAgendar} 
                                onPrazoChange={(e) => onPrazoChange('antecedenciaMinimaParaAgendar', e)} 
                                disabled={!isEditing}
                            />
                        </div>
                    </div>
                </fieldset>

                <fieldset className="form-section mt-6" disabled={!isEditing || isSaving}>
                    <legend className="form-section-title text-orange-700 border-orange-200">Regras de Cancelamento e Prazos</legend>
                    <div className="form-grid-container">
                        <div className="form-group md:col-span-2 pb-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                
                                <div className="space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">Permitir Cancelamento Online</label>
                                        <select 
                                            name="permiteCancelamentoOnline" 
                                            value={formData.permiteCancelamentoOnline} 
                                            onChange={handleChange} 
                                            className={`form-input ${!isEditing ? 'bg-gray-100 text-gray-600' : 'bg-white'}`}
                                            disabled={!isEditing}
                                        >
                                            <option value="Automático">Automático</option>
                                            <option value="Aprovação Necessária">Aprovação Necessária</option>
                                            <option value="Não Permitir">Não Permitir</option>
                                        </select>
                                    </div>

                                    {formData.permiteCancelamentoOnline !== 'Não Permitir' && (
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1 mt-3">Prazo Máximo p/ Cancelar Online</label>
                                            <PrazoInput 
                                                prazo={formData.prazoMaximoCancelamentoOnline} 
                                                onPrazoChange={(e) => onPrazoChange('prazoMaximoCancelamentoOnline', e)} 
                                                disabled={!isEditing}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">Ação de Cancelamento sem Pagamento</label>
                                        <select 
                                            name="acaoCancelamentoSemPagamento" 
                                            value={formData.acaoCancelamentoSemPagamento} 
                                            onChange={handleChange} 
                                            className={`form-input ${!isEditing ? 'bg-gray-100 text-gray-600' : 'bg-white'}`}
                                            disabled={!isEditing}
                                        >
                                            <option value="Ser Avisado">Manter na Agenda e Avisar Recepção</option>
                                            <option value="Cancelar Automaticamente">Cancelar Automaticamente</option>
                                        </select>
                                    </div>
                                    
                                    {formData.acaoCancelamentoSemPagamento === 'Cancelar Automaticamente' && (
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1 mt-3">Tempo limite sem pagamento</label>
                                            <PrazoInput 
                                                prazo={formData.prazoCancelamentoSemPagamento} 
                                                onPrazoChange={(e) => onPrazoChange('prazoCancelamentoSemPagamento', e)} 
                                                disabled={!isEditing}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
            </div>
        </div>
    );
};

export default ProfissionalAgendaSection;